'use client';

import type { FormEvent } from 'react';
import { useEffect, useState, useTransition } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { FormField } from '../../../components/ui/form-field';
import {
  AuthClientError,
  completeTwoFactorLogin,
  login,
  ssoStatus,
} from '../../../lib/auth/auth-client';

function formString(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === 'string' ? value : '';
}

export function LoginForm({
  returnTo,
  initialChallengeId,
}: {
  returnTo?: string;
  initialChallengeId?: string;
}) {
  const t = useTranslations('auth.login');
  const common = useTranslations('auth.common');
  const locale = useLocale();
  const router = useRouter();
  const [error, setError] = useState<string | undefined>();
  const [challengeId, setChallengeId] = useState<string | undefined>(initialChallengeId);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    void ssoStatus()
      .then((status) => setSsoEnabled(status.enabled))
      .catch(() => setSsoEnabled(false));
  }, []);

  function destination(user?: {
    mustChangePassword: boolean;
    mustEnrollTwoFactor: boolean;
  }): string {
    if (user?.mustChangePassword) return `/${locale}/change-password`;
    if (user?.mustEnrollTwoFactor) return `/${locale}/2fa`;
    if (returnTo?.startsWith(`/${locale}/`)) return returnTo;
    return `/${locale}/admin`;
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(undefined);
    const form = new FormData(event.currentTarget);
    const twoFactorCode = formString(form, 'twoFactorCode');
    const rememberDevice = form.has('rememberDevice');

    if (challengeId) {
      startTransition(() => {
        void completeTwoFactorLogin(challengeId, twoFactorCode, rememberDevice)
          .then((session) => {
            router.replace(destination(session.user));
            router.refresh();
          })
          .catch((caught: unknown) => {
            if (caught instanceof AuthClientError) setError(caught.message);
            else setError(common('unexpectedError'));
          });
      });
      return;
    }

    const identifier = formString(form, 'identifier');
    const password = formString(form, 'password');
    const captchaToken = formString(form, 'captchaToken');

    startTransition(() => {
      void login(identifier, password, {
        rememberMe: form.has('rememberMe'),
        rememberDevice,
        ...(captchaToken ? { captchaToken } : {}),
      })
        .then((result) => {
          if (result.status === 'two_factor_required') {
            setChallengeId(result.challenge.challengeId);
            return;
          }
          router.replace(destination(result.session.user));
          router.refresh();
        })
        .catch((caught: unknown) => {
          if (caught instanceof AuthClientError) {
            setError(caught.message);
            if (caught.captchaRequired) setCaptchaRequired(true);
          } else setError(common('unexpectedError'));
        });
    });
  }

  return (
    <Card className="auth-card">
      <form className="auth-form" method="post" onSubmit={onSubmit}>
        <div className="content-column">
          <p className="eyebrow">{common('secureArea')}</p>
          <h1>{t('title')}</h1>
          <p className="muted">{t('description')}</p>
        </div>
        {challengeId ? (
          <>
            <FormField
              name="twoFactorCode"
              label={t('twoFactorCode')}
              autoComplete="one-time-code"
              required
              dir="ltr"
              inputMode="numeric"
            />
            <label className="checkbox-row">
              <input name="rememberDevice" type="checkbox" />
              <span>{t('rememberDevice')}</span>
            </label>
          </>
        ) : (
          <>
            <FormField
              name="identifier"
              label={t('identifier')}
              autoComplete="username"
              required
              dir="ltr"
            />
            <FormField
              name="password"
              type="password"
              label={t('password')}
              autoComplete="current-password"
              required
              dir="ltr"
            />
            {captchaRequired ? (
              <FormField name="captchaToken" label={t('captcha')} required dir="ltr" />
            ) : null}
            <label className="checkbox-row">
              <input name="rememberMe" type="checkbox" />
              <span>{t('rememberMe')}</span>
            </label>
          </>
        )}
        {error ? (
          <p className="ui-field-error" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" disabled={isPending}>
          {isPending ? common('submitting') : challengeId ? t('twoFactorSubmit') : t('submit')}
        </Button>
        {ssoEnabled && !challengeId ? (
          <p>
            <a href={`/api/v1/auth/sso/start?returnTo=${encodeURIComponent(destination())}`}>
              {t('sso')}
            </a>
          </p>
        ) : null}
        <p className="muted">
          <Link href={`/${locale}/forgot-password`}>{t('forgotPassword')}</Link>
        </p>
      </form>
    </Card>
  );
}
